import { ArrowDown, MessageCircle } from "lucide-react";
import { LOGO_URL, buildWhatsAppLink } from "../lib/i18n";
import { useReveal } from "../hooks/useReveal";

const HERO_BG =
  "https://images.unsplash.com/photo-1761437855598-011cf89b2ad4?crop=entropy&cs=srgb&fm=jpg&q=85";

export const Hero = ({ t, lang }) => {
  const titleRef = useReveal({ threshold: 0.1 });
  const subRef = useReveal({ threshold: 0.1 });
  const ctaRef = useReveal({ threshold: 0.1 });

  return (
    <section
      data-testid="hero"
      id="home"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center grain"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover bg-hero-zoom"
          style={{ filter: "grayscale(100%) brightness(0.32) contrast(1.15)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.08),transparent_60%)]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 text-center pt-32 pb-24">
        {/* 3D Logo */}
        <div className="flex justify-center mb-10">
          <img
            data-testid="hero-logo"
            src={LOGO_URL}
            alt="Prestige Key"
            className="logo-3d w-44 sm:w-56 md:w-64 h-auto"
          />
        </div>

        {/* Eyebrow */}
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/60 mb-8">
          — {t.hero.eyebrow} —
        </p>

        {/* Title */}
        <h1
          data-testid="hero-title"
          ref={titleRef}
          className="reveal heading-display text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] mb-8 whitespace-pre-line"
        >
          {t.hero.title}
        </h1>

        <p
          ref={subRef}
          className="reveal text-sm sm:text-base uppercase tracking-[0.32em] text-white/70 mb-14"
          style={{ transitionDelay: "0.15s" }}
        >
          {t.hero.sub}
        </p>

        <div
          ref={ctaRef}
          className="reveal flex flex-col sm:flex-row items-center justify-center gap-4"
          style={{ transitionDelay: "0.3s" }}
        >
          <button
            data-testid="hero-cta-discover"
            onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-luxe"
          >
            {t.hero.cta}
            <ArrowDown size={14} />
          </button>
          <a
            data-testid="hero-cta-whatsapp"
            href={buildWhatsAppLink(
              lang === "fr"
                ? "Bonjour PRESTIGE KEY, je souhaite être contacté(e) pour une demande personnalisée."
                : "Hello PRESTIGE KEY, I would like to be contacted for a bespoke request."
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-luxe"
            style={{ background: "#fff", color: "#050505", borderColor: "#fff" }}
          >
            <MessageCircle size={14} />
            {t.hero.ctaSecondary}
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 bottom-8 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] uppercase tracking-[0.32em]">scroll</span>
        <span className="block w-px h-12 bg-white/30 overflow-hidden">
          <span className="block h-full w-full bg-white animate-[slow-zoom_2.4s_ease-in-out_infinite]" />
        </span>
      </div>

      {/* Marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/40 backdrop-blur-md py-3 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap text-[10px] tracking-[0.4em] text-white/50">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="px-10">
              {t.trust} ·
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
